import { Injectable, computed, signal } from '@angular/core';
import { ActingUser, PermissionModuleRef, SubUserSessionPayload } from '../models/customer-user.model';

/**
 * PermissionsService — session-level permission state for customer sub-users.
 *
 * A sub-user session carries `actingUser` + `permissions.modules` on the
 * login and /users/me responses; a plain customer session carries neither
 * and sees everything. Hydration is pushed in by UserService (login,
 * login-token, /me boot, support impersonation) so this service stays
 * dependency-free and safe to reference from any bundle.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private readonly _actingUser = signal<ActingUser | null>(null);

  /**
   * null = unrestricted (customer session — no permissions block at all).
   * A Set (even empty) = sub-user grant list. Presence of the permissions
   * block decides — an EMPTY module list is a valid "no modules" state and
   * must NOT fall back to unrestricted.
   */
  private readonly _modules = signal<ReadonlySet<string> | null>(null);

  readonly actingUser = this._actingUser.asReadonly();
  readonly isSubUser = computed(() => this._actingUser() !== null);

  readonly canProducts = computed(() => this.allows('PRODUCTS'));
  readonly canCart = computed(() => this.allows('CART'));
  readonly canOrders = computed(() => this.allows('ORDERS'));
  readonly canProjects = computed(() => this.allows('PROJECTS'));
  readonly canAlbumDesign = computed(() => this.allows('ALBUM_DESIGN'));
  readonly canGalleries = computed(() => this.allows('GALLERIES'));
  readonly canInvoices = computed(() => this.allows('INVOICES'));
  /** ACCOUNT is never grantable — main-account surfaces are customer-only. */
  readonly canAccount = computed(() => !this.isSubUser());

  hasModule(ref: PermissionModuleRef): boolean {
    return this.allows(ref);
  }

  /**
   * Hydrate from a login / /me / support-login payload. `actingUser`
   * presence is the contract's sub-user discriminator (guide §3.1) —
   * anything else (customer login, impersonation) clears to unrestricted.
   */
  hydrate(payload: SubUserSessionPayload | null | undefined): void {
    if (payload?.actingUser) {
      this._actingUser.set(payload.actingUser);
      this._modules.set(new Set(payload.permissions?.modules ?? []));
    } else {
      this.clear();
    }
  }

  clear(): void {
    this._actingUser.set(null);
    this._modules.set(null);
  }

  private allows(ref: PermissionModuleRef): boolean {
    const mods = this._modules();
    return mods === null || mods.has(ref);
  }
}
